import * as C from "../../config/constants.js";
import {
  getOverflowAlertY,
  getOverflowWarningY,
  isBodyOverlappingOverflowZone,
} from "./overflow.js";

/**
 * @returns {import("./overflow-state.js").OverflowState}
 */
export function createOverflowState() {
  return {
    warningDwellMs: 0,
    alertDwellMs: 0,
    lossDwellMs: 0,
    warningClearGraceMs: 0,
    alertClearGraceMs: 0,
    state: "safe",
  };
}

function cfgMs(key, fallback) {
  var v = C[key] != null ? Number(C[key]) : fallback;
  return isFinite(v) && v >= 0 ? v : fallback;
}

/**
 * @param {object} meta
 * @param {{ skipOverflowImmunity?: boolean }} [opts]
 */
export function onFlaskBodySpawned(meta, opts) {
  if (!meta) return;
  if (opts && opts.skipOverflowImmunity) return;
  meta.overflowImmunityMs = cfgMs("FLASK_OVERFLOW_BODY_IMMUNITY_MS", 2000);
}

export function isBodyOverflowEligible(meta) {
  if (!meta || !meta.body) return false;
  return meta.overflowImmunityMs == null || meta.overflowImmunityMs <= 0;
}

function isBodyEligible(meta) {
  return isBodyOverflowEligible(meta);
}

/**
 * Per-body zone for overflow glow UI (null = not in any zone or still immune).
 * @param {object|null|undefined} meta
 * @param {import("../flask-system.js").FlaskSystem|null|undefined} system
 * @returns {"warning"|"alert"|null}
 */
export function getBodyOverflowZoneTier(meta, system) {
  if (!isBodyEligible(meta) || !system) return null;
  var alertY = getOverflowAlertY(system);
  var warningY = getOverflowWarningY(system);
  if (isBodyOverlappingOverflowZone(meta, system, alertY)) return "alert";
  if (isBodyOverlappingOverflowZone(meta, system, warningY)) return "warning";
  return null;
}

/**
 * @param {import("../flask-system.js").FlaskSystem|null|undefined} system
 */
export function tickBodyImmunity(system, dtMs) {
  if (!system || !system.bodies) return;
  var dt = Math.max(0, dtMs);
  for (var i = 0; i < system.bodies.length; i++) {
    var meta = system.bodies[i];
    if (!meta || meta.overflowImmunityMs == null || meta.overflowImmunityMs <= 0) continue;
    meta.overflowImmunityMs = Math.max(0, meta.overflowImmunityMs - dt);
  }
}

/**
 * @param {import("../flask-system.js").FlaskSystem|null|undefined} system
 */
export function computeRawZones(system) {
  if (!system || !system.bodies || system.bodies.length === 0) {
    return { rawWarning: false, rawAlert: false };
  }
  var warningY = getOverflowWarningY(system);
  var alertY = getOverflowAlertY(system);
  var rawWarning = false;
  var rawAlert = false;
  for (var i = 0; i < system.bodies.length; i++) {
    var meta = system.bodies[i];
    if (!isBodyEligible(meta)) continue;
    if (isBodyOverlappingOverflowZone(meta, system, warningY)) rawWarning = true;
    if (isBodyOverlappingOverflowZone(meta, system, alertY)) rawAlert = true;
  }
  return { rawWarning: rawWarning, rawAlert: rawAlert };
}

function applyClearGrace(raw, clearGraceAccum, dtMs, clearGraceLimit) {
  if (raw) return { occupied: true, clearGraceMs: 0 };
  var next = clearGraceAccum + dtMs;
  return { occupied: next < clearGraceLimit, clearGraceMs: next };
}

/** True when NEXT DAY must stay disabled (alert zone dwell counting or alert active). */
export function isOverflowBlockingContinue(state) {
  if (!state) return false;
  if (state.state === "alert") return true;
  return state.alertDwellMs > 0;
}

/** True when overflow alert UI is flashing (swap NEXT DAY label for warning icon). */
export function isOverflowAlertFlashing(state) {
  return !!(state && state.state === "alert");
}

/**
 * @param {number} warningDwellMs
 * @param {number} alertDwellMs
 * @returns {"safe"|"warning"|"alert"}
 */
export function deriveOverflowState(warningDwellMs, alertDwellMs) {
  var warnThr = cfgMs("FLASK_OVERFLOW_WARNING_TRIGGER_MS", 1000);
  var alertThr = cfgMs("FLASK_OVERFLOW_ALERT_TRIGGER_MS", 2000);
  if (alertDwellMs >= alertThr) return "alert";
  if (warningDwellMs >= warnThr) return "warning";
  return "safe";
}

/**
 * @param {OverflowState|null|undefined} state
 */
export function resetOverflowPlayerAction(state) {
  if (!state) return;
  state.warningDwellMs = 0;
  state.alertDwellMs = 0;
  state.lossDwellMs = 0;
  state.warningClearGraceMs = 0;
  state.alertClearGraceMs = 0;
  state.state = "safe";
}

/**
 * @typedef {object} OverflowState
 * @property {number} warningDwellMs
 * @property {number} alertDwellMs
 * @property {number} lossDwellMs
 * @property {number} warningClearGraceMs
 * @property {number} alertClearGraceMs
 * @property {"safe"|"warning"|"alert"} state
 */

/**
 * @param {OverflowState|null|undefined} state
 * @param {import("../flask-system.js").FlaskSystem|null|undefined} system
 * @param {number} dtMs
 * @param {{ disabled?: boolean }} [opts]
 * @returns {{ state: OverflowState, gameOver: boolean }}
 */
export function tickOverflowState(state, system, dtMs, opts) {
  opts = opts || {};
  if (!state) state = createOverflowState();
  var dt = Math.max(0, isFinite(dtMs) ? dtMs : 0);

  if (opts.disabled || !system || typeof system.getBodyCount !== "function" || system.getBodyCount() <= 0) {
    resetOverflowPlayerAction(state);
    return { state: state, gameOver: false };
  }

  tickBodyImmunity(system, dt);
  var zones = computeRawZones(system);
  var clearGraceLimit = cfgMs("FLASK_OVERFLOW_ZONE_CLEAR_GRACE_MS", 100);

  var warnOcc = applyClearGrace(zones.rawWarning, state.warningClearGraceMs, dt, clearGraceLimit);
  state.warningClearGraceMs = warnOcc.clearGraceMs;
  var alertOcc = applyClearGrace(zones.rawAlert, state.alertClearGraceMs, dt, clearGraceLimit);
  state.alertClearGraceMs = alertOcc.clearGraceMs;

  if (warnOcc.occupied) state.warningDwellMs += dt;
  else state.warningDwellMs = 0;

  if (alertOcc.occupied) state.alertDwellMs += dt;
  else state.alertDwellMs = 0;

  state.state = deriveOverflowState(state.warningDwellMs, state.alertDwellMs);

  var lossThr = cfgMs("FLASK_OVERFLOW_LOSS_DWELL_MS", 7000);
  var gameOver = false;
  if (state.state === "alert") {
    state.lossDwellMs += dt;
    if (state.lossDwellMs >= lossThr) {
      resetOverflowPlayerAction(state);
      gameOver = true;
    }
  } else {
    state.lossDwellMs = 0;
  }

  return { state: state, gameOver: gameOver };
}